import React, { useState, useEffect, useCallback } from 'react'
import { logAdminAction } from '../../lib/adminAuth'
import { getSessionHeaders } from '../../lib/serverSession';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface SupportAgent {
  id: number
  user_id: number | null
  agent_name: string
  agent_email: string
  specialization: string
  department?: string
  supported_languages?: string[]
  max_concurrent_chats: number
  priority_level: number
  is_active: boolean
  created_at?: string
  updated_at?: string
  availability?: Array<{
    id: number
    agent_id: number
    status: 'available' | 'busy' | 'away' | 'offline'
    current_chat_count: number
    max_concurrent_chats: number
    last_activity?: string
  }>
}

interface SearchUser {
  id: number
  email: string
  full_name: string
  username: string
}

interface SupportAgentManagerProps {
  currentUser?: any
}

const SPECIALIZATION_OPTIONS = [
  { value: 'shareholder_support', label: 'Shareholder Support' },
  { value: 'affiliate_support', label: 'Affiliate Support' },
  { value: 'technical', label: 'Technical Support' },
  { value: 'billing', label: 'Billing Support' },
  { value: 'general', label: 'General Support' }
]

// v2.9.55: Department options matching the 5 consolidated categories
const DEPARTMENT_OPTIONS = [
  { value: 'general_support', label: '💬 General Support' },
  { value: 'account_finance', label: '💰 Account / Finance' },
  { value: 'technical_faults', label: '🔧 Technical / Faults' },
  { value: 'user_data_profile', label: '👤 User Data / Profile' },
  { value: 'commissions_referrals', label: '🤝 Commissions / Referrals' }
]

const LANGUAGE_OPTIONS = [
  { value: 'en', label: '🇬🇧 English' },
  { value: 'af', label: '🇿🇦 Afrikaans' },
  { value: 'zu', label: '🇿🇦 Zulu' },
  { value: 'xh', label: '🇿🇦 Xhosa' },
  { value: 'st', label: '🇿🇦 Sotho' },
  { value: 'pt', label: '🇵🇹 Portuguese' },
  { value: 'fr', label: '🇫🇷 French' },
  { value: 'sw', label: '🇰🇪 Swahili' },
  { value: 'ar', label: '🇸🇦 Arabic' },
  { value: 'es', label: '🇪🇸 Spanish' },
  { value: 'de', label: '🇩🇪 German' },
  { value: 'zh', label: '🇨🇳 Chinese' }
]

const ADMIN_ROLE_OPTIONS = [
  { value: 'support_admin', label: 'Support Admin' },
  { value: 'admin', label: 'Administrator' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'financial_admin', label: 'Financial Admin' },
  { value: 'read_only_admin', label: 'Read-Only Admin' }
]

const AGENT_CATEGORY_OPTIONS = [
  { value: 'support_agent', label: '🛎️ Support Agent' },
  { value: 'technical_agent', label: '🔧 Technical Agent' },
  { value: 'master_agent', label: '👑 Master Agent' }
]

// All admin dashboard pages available for assignment
const ADMIN_PAGES = [
  { id: 'todo', name: '📋 Task Dashboard', group: 'General' },
  { id: 'maintenance', name: '🔧 Maintenance Mode', group: 'System' },
  { id: 'users', name: '👤 User Management', group: 'Users' },
  { id: 'kyc', name: '📋 KYC Manager', group: 'Users' },
  { id: 'admin-kyc-completion', name: '✅ KYC Completion', group: 'Users' },
  { id: 'kyc-cleanup', name: '🧹 KYC Cleanup', group: 'Users' },
  { id: 'payments', name: '💳 Payment Management', group: 'Financial' },
  { id: 'payment-integrity', name: '🔍 Payment Integrity', group: 'Financial' },
  { id: 'payment-integrity-v2', name: '🔍 Payment Integrity V2', group: 'Financial' },
  { id: 'commissions', name: '💰 Commission Management', group: 'Financial' },
  { id: 'withdrawal-management', name: '🏧 Withdrawals', group: 'Financial' },
  { id: 'commission-conversions', name: '🔄 Commission Conversions', group: 'Financial' },
  { id: 'commission-cascade-audit', name: '🔗 Commission Cascade Audit', group: 'Financial' },
  { id: 'share-balance-audit', name: '⚖️ Share Balance Audit', group: 'Financial' },
  { id: 'master-audit', name: '📊 Master Audit', group: 'Financial' },
  { id: 'financial-audit', name: '📊 Financial Audit', group: 'Financial' },
  { id: 'reports', name: '📊 Share Ledger Reports', group: 'Financial' },
  { id: 'transaction-management', name: '🔄 Transaction Management', group: 'Financial' },
  { id: 'funds', name: '💵 Fund Management', group: 'Financial' },
  { id: 'wallets', name: '🏦 Wallet Management', group: 'Financial' },
  { id: 'invoices', name: '📄 Legal Invoices', group: 'Financial' },
  { id: 'certificates', name: '📜 Certificates', group: 'Content' },
  { id: 'sponsors', name: '🤝 Sponsors', group: 'Content' },
  { id: 'downline', name: '🌳 Downline', group: 'Content' },
  { id: 'competitions', name: '🏆 Competitions', group: 'Content' },
  { id: 'gallery', name: '📸 Gallery', group: 'Content' },
  { id: 'marketing', name: '📁 Marketing', group: 'Content' },
  { id: 'roadmap-journal', name: '📖 Roadmap Journal', group: 'Content' },
  { id: 'how-to-start', name: '🎥 Training Videos', group: 'Content' },
  { id: 'meetings', name: '📅 Meetings', group: 'Communication' },
  { id: 'list-manager', name: '📧 List Manager', group: 'Communication' },
  { id: 'mass-email', name: '📧 Mass Email', group: 'Communication' },
  { id: 'support', name: '🆘 Support Dashboard', group: 'Support' },
  { id: 'training', name: '🎓 Training Academy', group: 'Support' },
  { id: 'country-manager', name: '🌍 Country Manager', group: 'System' },
  { id: 'audit', name: '📋 Audit Logs', group: 'System' },
  { id: 'admin-users', name: '👨‍💼 Admin Users', group: 'System' },
  { id: 'role-permissions', name: '🔐 Roles & Permissions', group: 'System' },
  { id: 'session-manager', name: '🔒 Session Manager', group: 'System' },
  { id: 'security-admin', name: '🛡️ Security Dashboard', group: 'System' },
  { id: 'debug', name: '🐛 Debug System', group: 'System' },
]

const PAGE_GROUPS = ['General', 'Users', 'Financial', 'Content', 'Communication', 'Support', 'System']

const parseSpecializations = (spec: string | string[] | null | undefined): string[] => {
  if (!spec) return []
  if (Array.isArray(spec)) return spec.filter(Boolean)
  return spec.split(',').map(s => s.trim()).filter(Boolean)
}

const SupportAgentManager: React.FC<SupportAgentManagerProps> = ({ currentUser }) => {
  const [agents, setAgents] = useState<SupportAgent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingAgent, setEditingAgent] = useState<SupportAgent | null>(null)

  // User search state
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [userSearchResults, setUserSearchResults] = useState<SearchUser[]>([])
  const [selectedUser, setSelectedUser] = useState<SearchUser | null>(null)
  const [searchingUsers, setSearchingUsers] = useState(false)
  const [createMode, setCreateMode] = useState<'new' | 'existing'>('new')

  const emptyForm = {
    agent_name: '',
    agent_email: '',
    password: '',
    admin_role: 'support_admin',
    agent_category: 'support_agent',
    specialization: [] as string[],
    department: 'general_support',
    supported_languages: ['en'] as string[],
    max_concurrent_chats: 3,
    priority_level: 1,
    page_access: [] as string[]
  }
  const [formData, setFormData] = useState(emptyForm)

  useEffect(() => {
    loadAgents()
  }, [])

  const loadAgents = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/support`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'load_agents' })
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to load agents')
      setAgents(result.data || [])
    } catch (err: any) {
      console.error('Error loading agents:', err)
      setError(err.message || 'Failed to load agents')
    } finally {
      setLoading(false)
    }
  }

  // Search existing users
  const handleSearchUsers = useCallback(async (query: string) => {
    setUserSearchQuery(query)
    if (query.trim().length < 2) { setUserSearchResults([]); return }
    setSearchingUsers(true)
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/support`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'search_users', data: { query: query.trim() } })
      })
      const result = await response.json()
      if (response.ok) setUserSearchResults(result.data || [])
    } catch (err) { console.error('User search error:', err) }
    finally { setSearchingUsers(false) }
  }, [])

  const handleSelectUser = (user: SearchUser) => {
    setSelectedUser(user)
    setFormData(prev => ({
      ...prev,
      agent_name: user.full_name || user.username || '',
      agent_email: user.email || ''
    }))
    setUserSearchResults([])
    setUserSearchQuery('')
  }

  const handleSaveAgent = async () => {
    if (!formData.agent_name.trim() || !formData.agent_email.trim()) {
      setError('Agent name and email are required')
      return
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.agent_email.trim())) {
      setError('Please enter a valid email address')
      return
    }
    // Password required for new agents (not editing)
    if (!editingAgent && createMode === 'new' && !formData.password) {
      setError('Password is required for new agents')
      return
    }

    setLoading(true)
    setError(null)
    try {
      const isEditing = !!editingAgent
      const operation = isEditing ? 'update_agent' : 'create_agent'
      const payload: any = isEditing
        ? { agentId: editingAgent!.id, ...formData, specialization: formData.specialization }
        : {
            ...formData,
            specialization: formData.specialization,
            existing_user_id: createMode === 'existing' && selectedUser ? selectedUser.id : undefined
          }

      const response = await fetch(`${API_BASE_URL}/api/admin/support`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation, data: payload })
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.error || `Failed to ${isEditing ? 'update' : 'create'} agent`)

      await logAdminAction(
        currentUser?.email || 'unknown',
        isEditing ? 'UPDATE_SUPPORT_AGENT' : 'CREATE_SUPPORT_AGENT',
        'support_agents',
        isEditing ? editingAgent!.id.toString() : result.data?.id?.toString(),
        { agent_name: formData.agent_name, agent_email: formData.agent_email }
      )

      setFormData(emptyForm)
      setShowCreateForm(false)
      setEditingAgent(null)
      setSelectedUser(null)
      setCreateMode('new')
      await loadAgents()
    } catch (err: any) {
      console.error('Error saving agent:', err)
      setError(err.message || 'Failed to save agent')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleAgentStatus = async (agent: SupportAgent) => {
    setLoading(true)
    setError(null)
    try {
      const newStatus = !agent.is_active

      const response = await fetch(`${API_BASE_URL}/api/admin/support`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'toggle_agent_status',
          data: { agentId: agent.id, newStatus }
        })
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to toggle status')

      await logAdminAction(
        currentUser?.email || 'unknown',
        newStatus ? 'ACTIVATE_SUPPORT_AGENT' : 'DEACTIVATE_SUPPORT_AGENT',
        'support_agents',
        agent.id.toString(),
        { agent_name: agent.agent_name, new_status: newStatus }
      )

      await loadAgents()
    } catch (err: any) {
      console.error('Error toggling agent status:', err)
      setError(err.message || 'Failed to update agent status')
    } finally {
      setLoading(false)
    }
  }

  const handleEditAgent = (agent: SupportAgent) => {
    setEditingAgent(agent)
    setFormData({
      agent_name: agent.agent_name || '',
      agent_email: agent.agent_email || '',
      password: '',
      admin_role: 'support_admin',
      agent_category: (agent as any).agent_category || 'support_agent',
      specialization: parseSpecializations(agent.specialization),
      department: agent.department || 'general_support',
      supported_languages: Array.isArray(agent.supported_languages) ? agent.supported_languages : ['en'],
      max_concurrent_chats: agent.max_concurrent_chats || 3,
      priority_level: agent.priority_level || 1,
      page_access: Array.isArray((agent as any).page_access) ? (agent as any).page_access : []
    })
    setShowCreateForm(true)
    setCreateMode('new')
    setSelectedUser(null)
    setError(null)
  }

  // Delete agent state
  const [deleteModal, setDeleteModal] = useState<{
    agent: SupportAgent
    ticketCount: number
    openTicketCount: number
    availableAgents: Array<{ id: number; agent_name: string; agent_email: string; is_active: boolean }>
  } | null>(null)
  const [reassignToId, setReassignToId] = useState<number | ''>('')
  const [deleting, setDeleting] = useState(false)

  const handleDeleteAgent = async (agent: SupportAgent) => {
    if (!window.confirm(`Are you sure you want to DELETE agent "${agent.agent_name}"?\n\nThis will remove their support agent record and deactivate their admin access.\nTheir user account and shares will NOT be affected.`)) return
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/support`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'delete_agent', data: { agentId: agent.id } })
      })
      const result = await response.json()

      // If tickets need reassignment, show the modal
      if (response.status === 409 && result.error === 'TICKETS_NEED_REASSIGNMENT') {
        setDeleteModal({
          agent,
          ticketCount: result.ticketCount,
          openTicketCount: result.openTicketCount,
          availableAgents: result.availableAgents || []
        })
        setReassignToId('')
        setLoading(false)
        return
      }

      if (!response.ok) throw new Error(result.error || 'Failed to delete agent')
      await logAdminAction(currentUser?.email || 'unknown', 'DELETE_SUPPORT_AGENT', 'support_agents', agent.id.toString(), { agent_name: agent.agent_name })
      await loadAgents()
    } catch (err: any) {
      console.error('Error deleting agent:', err)
      setError(err.message || 'Failed to delete agent')
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmDeleteWithReassign = async () => {
    if (!deleteModal || !reassignToId) return
    setDeleting(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/support`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'delete_agent',
          data: { agentId: deleteModal.agent.id, reassignToAgentId: reassignToId }
        })
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to delete agent')
      await logAdminAction(
        currentUser?.email || 'unknown',
        'DELETE_SUPPORT_AGENT',
        'support_agents',
        deleteModal.agent.id.toString(),
        { agent_name: deleteModal.agent.agent_name, tickets_reassigned: result.ticketsReassigned, reassigned_to: reassignToId }
      )
      setDeleteModal(null)
      setReassignToId('')
      await loadAgents()
    } catch (err: any) {
      console.error('Error deleting agent:', err)
      setError(err.message || 'Failed to delete agent')
    } finally {
      setDeleting(false)
    }
  }

  const handleSpecializationChange = (specialization: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      specialization: checked
        ? [...prev.specialization, specialization]
        : prev.specialization.filter(s => s !== specialization)
    }))
  }

  const handleCloseForm = () => {
    setShowCreateForm(false)
    setEditingAgent(null)
    setFormData(emptyForm)
    setSelectedUser(null)
    setCreateMode('new')
    setUserSearchQuery('')
    setUserSearchResults([])
    setError(null)
  }

  const __getStatusColor = (status?: string) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800'
      case 'busy': return 'bg-yellow-100 text-yellow-800'
      case 'away': return 'bg-orange-100 text-orange-800'
      case 'offline': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const __formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Dark theme styles
  const s = {
    bg: '#111827', card: '#1F2937', cardHover: '#374151', border: '#4B5563',
    text: '#F9FAFB', textMuted: '#9CA3AF', textDim: '#6B7280',
    input: '#374151', inputBorder: '#4B5563', inputFocus: '#D97706',
    accent: '#D97706', accentHover: '#B45309',
    green: '#10B981', red: '#EF4444', blue: '#3B82F6'
  }

  if (loading && agents.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
      </div>
    )
  }

  return (
    <div style={{ color: s.text }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: s.text, margin: 0 }}>Support Agent Management</h2>
          <p style={{ color: s.textMuted, fontSize: 14, margin: '4px 0 0' }}>Manage support agents, availability, and specializations</p>
        </div>
        <button
          onClick={() => { setEditingAgent(null); setFormData(emptyForm); setShowCreateForm(true); setSelectedUser(null); setCreateMode('new'); setError(null) }}
          style={{ background: s.accent, color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
        >
          + Add New Agent
        </button>
      </div>

      {/* Error Banner */}
      {error && (
        <div style={{ background: '#7F1D1D', border: '1px solid #991B1B', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#FCA5A5', fontSize: 14 }}>{error}</span>
          <button onClick={() => setError(null)} style={{ color: '#FCA5A5', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>✕</button>
        </div>
      )}

      {/* Create/Edit Agent Modal */}
      {showCreateForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
          <div style={{ background: s.card, borderRadius: 12, maxWidth: 520, width: '100%', padding: 24, border: `1px solid ${s.border}`, maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: s.text, marginBottom: 20 }}>
              {editingAgent ? 'Edit Support Agent' : 'Add New Support Agent'}
            </h3>

            {/* Mode Toggle (only for new agents) */}
            {!editingAgent && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                <button
                  onClick={() => { setCreateMode('new'); setSelectedUser(null) }}
                  style={{ flex: 1, padding: '10px 16px', borderRadius: 8, border: `1px solid ${createMode === 'new' ? s.accent : s.border}`, background: createMode === 'new' ? s.accent : 'transparent', color: createMode === 'new' ? '#fff' : s.textMuted, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
                >
                  🆕 Create New User
                </button>
                <button
                  onClick={() => setCreateMode('existing')}
                  style={{ flex: 1, padding: '10px 16px', borderRadius: 8, border: `1px solid ${createMode === 'existing' ? s.accent : s.border}`, background: createMode === 'existing' ? s.accent : 'transparent', color: createMode === 'existing' ? '#fff' : s.textMuted, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
                >
                  👤 Existing User
                </button>
              </div>
            )}

            {/* User Search (existing user mode) */}
            {createMode === 'existing' && !editingAgent && (
              <div style={{ marginBottom: 16, position: 'relative' }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: s.textMuted, marginBottom: 6 }}>Search Existing Users</label>
                <input
                  type="text"
                  value={userSearchQuery}
                  onChange={(e) => handleSearchUsers(e.target.value)}
                  placeholder="Search by email, name, or username..."
                  style={{ width: '100%', padding: '10px 12px', background: s.input, border: `1px solid ${s.inputBorder}`, borderRadius: 8, color: s.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                />
                {searchingUsers && <div style={{ color: s.textMuted, fontSize: 12, marginTop: 4 }}>Searching...</div>}
                {userSearchResults.length > 0 && (
                  <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 10, background: s.cardHover, border: `1px solid ${s.border}`, borderRadius: 8, maxHeight: 200, overflowY: 'auto', marginTop: 4 }}>
                    {userSearchResults.map(u => (
                      <div
                        key={u.id}
                        onClick={() => handleSelectUser(u)}
                        style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: `1px solid ${s.border}`, color: s.text, fontSize: 13 }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = s.card)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        <div style={{ fontWeight: 600 }}>{u.full_name || u.username || 'No name'}</div>
                        <div style={{ color: s.textDim, fontSize: 12 }}>{u.email} {u.username ? `(@${u.username})` : ''}</div>
                      </div>
                    ))}
                  </div>
                )}
                {selectedUser && (
                  <div style={{ marginTop: 8, padding: '10px 12px', background: '#064E3B', border: '1px solid #10B981', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ color: '#A7F3D0', fontWeight: 600, fontSize: 14 }}>✓ {selectedUser.full_name || selectedUser.username}</div>
                      <div style={{ color: '#6EE7B7', fontSize: 12 }}>{selectedUser.email}</div>
                    </div>
                    <button onClick={() => { setSelectedUser(null); setFormData(prev => ({ ...prev, agent_name: '', agent_email: '' })) }} style={{ color: '#6EE7B7', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>✕</button>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Agent Name */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: s.textMuted, marginBottom: 6 }}>Agent Name *</label>
                <input type="text" value={formData.agent_name} onChange={(e) => setFormData(prev => ({ ...prev, agent_name: e.target.value }))}
                  placeholder="Display name for the agent"
                  style={{ width: '100%', padding: '10px 12px', background: s.input, border: `1px solid ${s.inputBorder}`, borderRadius: 8, color: s.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              {/* Agent Email */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: s.textMuted, marginBottom: 6 }}>Agent Email *</label>
                <input type="email" value={formData.agent_email} onChange={(e) => setFormData(prev => ({ ...prev, agent_email: e.target.value }))}
                  placeholder="agent@aureusafrica.com"
                  style={{ width: '100%', padding: '10px 12px', background: s.input, border: `1px solid ${s.inputBorder}`, borderRadius: 8, color: s.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              {/* Password (new agents only) */}
              {!editingAgent && (
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: s.textMuted, marginBottom: 6 }}>
                    Password {createMode === 'new' ? '*' : '(optional - set new password)'}
                  </label>
                  <input type="password" value={formData.password} onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Min 8 characters"
                    style={{ width: '100%', padding: '10px 12px', background: s.input, border: `1px solid ${s.inputBorder}`, borderRadius: 8, color: s.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
              )}
              {/* Admin Role */}
              {!editingAgent && (
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: s.textMuted, marginBottom: 6 }}>Admin Role</label>
                  <select value={formData.admin_role} onChange={(e) => setFormData(prev => ({ ...prev, admin_role: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', background: s.input, border: `1px solid ${s.inputBorder}`, borderRadius: 8, color: s.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                  >
                    {ADMIN_ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              )}
              {/* Agent Category */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: s.textMuted, marginBottom: 6 }}>Agent Category</label>
                <select value={formData.agent_category} onChange={(e) => setFormData(prev => ({ ...prev, agent_category: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', background: s.input, border: `1px solid ${s.inputBorder}`, borderRadius: 8, color: s.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                >
                  {AGENT_CATEGORY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              {/* Department */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: s.textMuted, marginBottom: 6 }}>Department</label>
                <select value={formData.department} onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', background: s.input, border: `1px solid ${s.inputBorder}`, borderRadius: 8, color: s.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                >
                  {DEPARTMENT_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
              {/* Supported Languages */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: s.textMuted, marginBottom: 6 }}>
                  Supported Languages <span style={{ fontWeight: 400, fontSize: 11 }}>({formData.supported_languages.length} selected)</span>
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {LANGUAGE_OPTIONS.map((lang) => {
                    const checked = formData.supported_languages.includes(lang.value)
                    return (
                      <label key={lang.value} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: s.text, padding: '4px 8px', background: checked ? '#064E3B' : s.input, borderRadius: 6, border: `1px solid ${checked ? '#10B981' : s.inputBorder}` }}>
                        <input type="checkbox" checked={checked}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              supported_languages: e.target.checked
                                ? [...prev.supported_languages, lang.value]
                                : prev.supported_languages.filter(l => l !== lang.value)
                            }))
                          }}
                          style={{ accentColor: '#10B981' }} />
                        {lang.label}
                      </label>
                    )
                  })}
                </div>
              </div>
              {/* Page Access - choose which admin pages this agent can see */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: s.textMuted, marginBottom: 6 }}>
                  Page Access <span style={{ fontWeight: 400, fontSize: 11 }}>({formData.page_access.length} pages selected)</span>
                </label>
                <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, page_access: ADMIN_PAGES.map(p => p.id) }))}
                    style={{ fontSize: 11, padding: '3px 10px', background: '#1E3A5F', color: '#93C5FD', border: '1px solid #2563EB', borderRadius: 6, cursor: 'pointer' }}>
                    Select All
                  </button>
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, page_access: [] }))}
                    style={{ fontSize: 11, padding: '3px 10px', background: '#1F2937', color: '#ffffff', border: '1px solid #374151', borderRadius: 6, cursor: 'pointer' }}>
                    Clear All
                  </button>
                </div>
                <div style={{ maxHeight: 280, overflowY: 'auto', border: `1px solid ${s.inputBorder}`, borderRadius: 8, background: s.input }}>
                  {PAGE_GROUPS.map(group => {
                    const groupPages = ADMIN_PAGES.filter(p => p.group === group)
                    if (groupPages.length === 0) return null
                    return (
                      <div key={group}>
                        <div style={{ padding: '6px 12px', background: 'rgba(255,215,0,0.08)', fontSize: 11, fontWeight: 700, color: s.accent, textTransform: 'uppercase', letterSpacing: 1, borderBottom: `1px solid ${s.inputBorder}` }}>
                          {group}
                        </div>
                        {groupPages.map(page => {
                          const checked = formData.page_access.includes(page.id)
                          return (
                            <label key={page.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', cursor: 'pointer', borderBottom: `1px solid ${s.inputBorder}`, background: checked ? 'rgba(16,185,129,0.08)' : 'transparent' }}>
                              <input type="checkbox" checked={checked}
                                onChange={(e) => {
                                  setFormData(prev => ({
                                    ...prev,
                                    page_access: e.target.checked
                                      ? [...prev.page_access, page.id]
                                      : prev.page_access.filter(id => id !== page.id)
                                  }))
                                }}
                                style={{ accentColor: s.accent }} />
                              <span style={{ fontSize: 13, color: checked ? '#6EE7B7' : s.text }}>{page.name}</span>
                            </label>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              </div>
              {/* Specializations */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: s.textMuted, marginBottom: 6 }}>Specializations</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {SPECIALIZATION_OPTIONS.map((spec) => (
                    <label key={spec.value} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: s.text, padding: '4px 8px', background: formData.specialization.includes(spec.value) ? '#92400E' : s.input, borderRadius: 6, border: `1px solid ${formData.specialization.includes(spec.value) ? s.accent : s.inputBorder}` }}>
                      <input type="checkbox" checked={formData.specialization.includes(spec.value)} onChange={(e) => handleSpecializationChange(spec.value, e.target.checked)} style={{ accentColor: s.accent }} />
                      {spec.label}
                    </label>
                  ))}
                </div>
              </div>
              {/* Max Chats & Priority */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: s.textMuted, marginBottom: 6 }}>Max Concurrent Chats</label>
                  <input type="number" min="1" max="20" value={formData.max_concurrent_chats}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_concurrent_chats: parseInt(e.target.value) || 3 }))}
                    style={{ width: '100%', padding: '10px 12px', background: s.input, border: `1px solid ${s.inputBorder}`, borderRadius: 8, color: s.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: s.textMuted, marginBottom: 6 }}>Priority Level</label>
                  <select value={formData.priority_level} onChange={(e) => setFormData(prev => ({ ...prev, priority_level: parseInt(e.target.value) }))}
                    style={{ width: '100%', padding: '10px 12px', background: s.input, border: `1px solid ${s.inputBorder}`, borderRadius: 8, color: s.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                  >
                    <option value={1}>1 (Highest)</option>
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                    <option value={4}>4</option>
                    <option value={5}>5 (Lowest)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Modal Buttons */}
            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button onClick={handleCloseForm}
                style={{ flex: 1, padding: '10px 16px', background: s.cardHover, color: s.textMuted, border: `1px solid ${s.border}`, borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
              >Cancel</button>
              <button onClick={handleSaveAgent}
                disabled={!formData.agent_name.trim() || !formData.agent_email.trim() || loading}
                style={{ flex: 1, padding: '10px 16px', background: s.accent, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14, opacity: (!formData.agent_name.trim() || !formData.agent_email.trim() || loading) ? 0.5 : 1 }}
              >{loading ? 'Saving...' : editingAgent ? 'Update Agent' : 'Create Agent'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Agents List */}
      {agents.length === 0 && !loading ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>👥</div>
          <h3 style={{ fontSize: 18, fontWeight: 600, color: s.text, marginBottom: 8 }}>No Support Agents</h3>
          <p style={{ color: s.textMuted, marginBottom: 16 }}>No support agents have been configured yet.</p>
          <button onClick={() => { setEditingAgent(null); setFormData(emptyForm); setShowCreateForm(true) }}
            style={{ background: s.accent, color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
            Add First Agent
          </button>
        </div>
      ) : (
        <div style={{ background: s.card, borderRadius: 12, border: `1px solid ${s.border}`, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${s.border}` }}>
                  {['Agent', 'Department', 'Languages', 'Status', 'Capacity', 'Priority', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: s.textDim, textTransform: 'uppercase', letterSpacing: 1 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {agents.map((agent) => {
                  const avail = agent.availability?.[0]
                  const __specs = parseSpecializations(agent.specialization)
                  return (
                    <tr key={agent.id} style={{ borderBottom: `1px solid ${s.border}`, opacity: agent.is_active ? 1 : 0.5 }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ width: 36, height: 36, borderRadius: '50%', background: agent.is_active ? '#92400E' : s.cardHover, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, color: agent.is_active ? '#FDE68A' : s.textDim }}>
                            {(agent.agent_name || '?').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14, color: s.text }}>{agent.agent_name}</div>
                            <div style={{ fontSize: 12, color: s.textDim }}>{agent.agent_email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: '#78350F', color: '#FDE68A' }}>
                          {(agent.department || 'general_support').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {(Array.isArray(agent.supported_languages) ? agent.supported_languages : ['en']).map((lang: string) => {
                            const langInfo = LANGUAGE_OPTIONS.find(l => l.value === lang)
                            return (
                              <span key={lang} style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: '#064E3B', color: '#6EE7B7' }}>
                                {langInfo?.label || lang}
                              </span>
                            )
                          })}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                          background: avail?.status === 'available' ? '#064E3B' : avail?.status === 'busy' ? '#78350F' : '#1F2937',
                          color: avail?.status === 'available' ? '#6EE7B7' : avail?.status === 'busy' ? '#FDE68A' : s.textDim }}>
                          {avail?.status || 'offline'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 14, color: s.text }}>
                        {avail?.current_chat_count || 0} / {agent.max_concurrent_chats || 3}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 14, color: s.text }}>
                        Level {agent.priority_level || 1}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: 10 }}>
                          <button onClick={() => handleEditAgent(agent)} style={{ background: 'none', border: 'none', color: s.blue, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Edit</button>
                          <button onClick={() => handleToggleAgentStatus(agent)} style={{ background: 'none', border: 'none', color: agent.is_active ? '#F59E0B' : s.green, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                            {agent.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button onClick={() => handleDeleteAgent(agent)} style={{ background: 'none', border: 'none', color: s.red, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Ticket Reassignment Modal */}
      {deleteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
          <div style={{ background: s.card, borderRadius: 12, maxWidth: 480, width: '100%', padding: 24, border: `1px solid ${s.border}` }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: s.red, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              ⚠️ Tickets Need Reassignment
            </h3>

            <div style={{ background: '#7C2D1240', border: '1px solid #991B1B', borderRadius: 8, padding: 16, marginBottom: 20 }}>
              <p style={{ color: '#FCA5A5', fontSize: 14, margin: 0 }}>
                <strong>{deleteModal.agent.agent_name}</strong> has <strong>{deleteModal.ticketCount}</strong> ticket{deleteModal.ticketCount !== 1 ? 's' : ''} assigned
                {deleteModal.openTicketCount > 0 && <> ({deleteModal.openTicketCount} still open)</>}.
              </p>
              <p style={{ color: '#FCA5A5', fontSize: 13, margin: '8px 0 0' }}>
                Select an agent below to reassign all tickets before deletion.
              </p>
            </div>

            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: s.text, marginBottom: 8 }}>
              Reassign tickets to:
            </label>
            {deleteModal.availableAgents.length > 0 ? (
              <select
                value={reassignToId}
                onChange={(e) => setReassignToId(e.target.value ? Number(e.target.value) : '')}
                style={{ width: '100%', padding: '10px 12px', background: s.input, color: s.text, border: `1px solid ${s.inputBorder}`, borderRadius: 8, fontSize: 14, marginBottom: 20 }}
              >
                <option value="">-- Select an agent --</option>
                {deleteModal.availableAgents.map(a => (
                  <option key={a.id} value={a.id}>{a.agent_name} ({a.agent_email})</option>
                ))}
              </select>
            ) : (
              <div style={{ background: '#92400E40', border: '1px solid #92400E', borderRadius: 8, padding: 12, marginBottom: 20 }}>
                <p style={{ color: '#FDE68A', fontSize: 13, margin: 0 }}>
                  ⚠️ No other active agents available. Please create or activate another agent first.
                </p>
              </div>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => { setDeleteModal(null); setReassignToId('') }}
                style={{ flex: 1, padding: '10px 16px', background: s.cardHover, color: s.textMuted, border: `1px solid ${s.border}`, borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeleteWithReassign}
                disabled={!reassignToId || deleting}
                style={{
                  flex: 1, padding: '10px 16px', background: s.red, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14,
                  opacity: (!reassignToId || deleting) ? 0.5 : 1
                }}
              >
                {deleting ? 'Deleting...' : `Reassign & Delete`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SupportAgentManager